import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BadgeCheck, ShieldCheck, ShieldX, Hourglass, EyeOff, ExternalLink,
  FileWarning, RefreshCw, Mail, Stethoscope, IdCard, Clock,
} from 'lucide-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';
import { formatTime } from '../utils/clock';
import CommonLoader from '../components/CommonLoader';

// OPEN-PLATFORM DOCTOR REVIEW — the reviewer's side of Backend's
// doctorReviewController. Route-gated by OpenPlatformRoute (fails CLOSED) and
// re-gated server-side by requireOpenPlatformAdmin, which re-reads
// isOpenPlatform from the DB rather than trusting the token.
//
// WHY THIS SCREEN CARRIES SO MUCH EXPLANATION.
// A doctor on Tenant #1 has no hospital vouching for them, so the MOC team
// vouches instead — and until someone here clicks Approve, that doctor is
// INVISIBLE to every patient browsing the platform-wide directory, no matter
// how complete their profile is (directoryController filters on
// reviewStatus === 'approved', never on status === 'active'). The backend for
// this shipped with no UI at all, which meant submissions piled up unanswered
// and the doctors involved had no way to know. Whoever opens this cold has to
// understand that inaction is itself a decision — hence the banner, not just a
// table.

const MySwal = withReactContent(Swal);

// The server presigns ID proofs at fetch time with a 1h TTL
// (S3_SIGNED_URL_TTL_SECONDS, default 3600). We treat them as spent a little
// early: a link that 403s in a fresh tab looks like a broken system, whereas a
// pre-emptive refetch is invisible. See openIdProof below.
const SIGNED_URL_LIFETIME_MS = 55 * 60 * 1000;

const TAB_IDS = ['pending_review', 'approved', 'rejected'];

const STATUS_STYLES = {
  pending_review: { icon: Hourglass, cls: 'bg-amber-50 text-amber-700' },
  approved: { icon: ShieldCheck, cls: 'bg-emerald-50 text-emerald-700' },
  rejected: { icon: ShieldX, cls: 'bg-rose-50 text-rose-700' },
};

// `specialty` is a JSONB mixed array on the Doctor model — historically an array
// of { specialtyId, name }, but older rows hold plain strings and some hold
// nothing. Render every shape rather than printing "[object Object]".
const specialtyLabel = (specialty) => {
  if (!specialty) return '';
  if (typeof specialty === 'string') return specialty;
  if (!Array.isArray(specialty)) return specialty.name || specialty.specialtyName || '';
  return specialty
    .map((s) => (typeof s === 'string' ? s : s?.name || s?.specialtyName || ''))
    .filter(Boolean)
    .join(', ');
};

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  // Date as before (dd/mm/yyyy); the time in the one clock format, "2:30 PM".
  return Number.isNaN(d.getTime()) ? '' : `${d.toLocaleDateString('en-GB')}, ${formatTime(d)}`;
};

const StatusPill = ({ status, label }) => {
  const { icon: Icon, cls } = STATUS_STYLES[status] || STATUS_STYLES.pending_review;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
};

// ID PROOF ACCESS. Three outcomes, and only one of them is a link:
//  - no idProofKey at all      -> nothing was ever uploaded; a link would lie
//  - key present, no URL       -> presigning failed server-side; refetching may fix it
//  - URL present               -> a real anchor (target=_blank), so the browser
//                                 handles it natively and no popup blocker fires
// Never render an <a> with an empty href: it looks clickable, does nothing, and
// the reviewer concludes the document is missing when it may not be.
const IdProofCell = ({ doctor, onOpen, t }) => {
  if (!doctor.idProofKey) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-400" title={t('doctorReview.id_proof_missing_hint', 'This doctor submitted without an ID proof on file.')}>
        <FileWarning className="w-3.5 h-3.5" />
        {t('doctorReview.id_proof_missing', 'No ID proof')}
      </span>
    );
  }

  if (!doctor.idProofUrl) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-800"
        title={t('doctorReview.id_proof_unavailable_hint', 'The file exists but its secure link could not be generated. Refresh to try again.')}
      >
        <RefreshCw className="w-3.5 h-3.5" />
        {t('doctorReview.id_proof_unavailable', 'Link unavailable — refresh')}
      </button>
    );
  }

  return (
    <a
      href={doctor.idProofUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onOpen}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2D8A8A] hover:text-[#1a365d]"
    >
      <ExternalLink className="w-3.5 h-3.5" />
      {t('doctorReview.id_proof_open', 'Open ID proof')}
    </a>
  );
};

// Who decided, when, and why — the reviewer's own audit trail on the Approved
// and Rejected tabs. Renders nothing on a row that has not been decided.
const DecisionRow = ({ doctor }) => {
  if (!doctor.reviewedAt && !doctor.reviewNote) return null;
  return (
    <div className="mt-1.5 text-xs text-gray-500">
      {doctor.reviewedAt && (
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3 h-3 text-gray-400" />
          {formatDateTime(doctor.reviewedAt)}
          {/* reviewedBy is the reviewer's user id — not drawn (owner, 29 Sep 2026). */}
          {doctor.reviewedByName ? ` · ${doctor.reviewedByName}` : ''}
        </span>
      )}
      {doctor.reviewNote && (
        <div className="mt-1 text-gray-600 italic">“{doctor.reviewNote}”</div>
      )}
    </div>
  );
};

export default function DoctorReview() {
  const { t } = useTranslation();
  const axiosInstance = useAxiosAuth();

  const [status, setStatus] = useState('pending_review');
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // When the presigned ID-proof URLs in `doctors` were minted. Used to decide
  // whether a link is worth clicking or needs re-signing first.
  const [fetchedAt, setFetchedAt] = useState(0);
  const [decidingId, setDecidingId] = useState(null);

  const TABS = useMemo(() => ([
    { id: 'pending_review', label: t('doctorReview.tab_pending', 'Pending') },
    { id: 'approved', label: t('doctorReview.tab_approved', 'Approved') },
    { id: 'rejected', label: t('doctorReview.tab_rejected', 'Rejected') },
  ]), [t]);

  const statusLabel = useCallback(
    (id) => TABS.find((tab) => tab.id === id)?.label || id,
    [TABS],
  );

  const fetchQueue = useCallback(async (nextStatus, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axiosInstance.get(`${baseURL}/doctor-review/queue`, {
        params: { status: nextStatus },
      });
      setDoctors(Array.isArray(data?.doctors) ? data.doctors : []);
      setFetchedAt(Date.now());
      setLoadError('');
      return true;
    } catch (err) {
      const message = err?.response?.data?.message
        || t('doctorReview.load_error', 'Failed to load the review queue.');
      setLoadError(typeof message === 'string' ? message : t('doctorReview.load_error', 'Failed to load the review queue.'));
      setDoctors([]);
      return false;
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => { fetchQueue(status); }, [status, fetchQueue]);

  // The anchor's own target=_blank does the opening — this only intercepts the
  // cases where following the href would fail: a URL old enough to have expired,
  // or one that never got signed. In both we re-sign the whole page in place and
  // ask for a second click, rather than calling window.open() after an await
  // (which popup blockers treat as unprovoked and swallow silently).
  const openIdProof = useCallback(async (doctor, event) => {
    const stale = !doctor.idProofUrl || (Date.now() - fetchedAt) > SIGNED_URL_LIFETIME_MS;
    if (!stale) return;

    event?.preventDefault();
    const ok = await fetchQueue(status, { silent: true });
    if (ok) {
      toast.info(t('doctorReview.id_proof_refreshed', 'That secure link had expired. Refreshed — open the ID proof again.'));
    } else {
      toast.error(t('doctorReview.id_proof_refresh_failed', 'Could not refresh the ID-proof link. Try again in a moment.'));
    }
  }, [fetchedAt, fetchQueue, status, t]);

  const decide = useCallback(async (doctor, approve) => {
    const fullName = `${doctor.name || ''} ${doctor.surname || ''}`.trim() || t('common.doctor_label', 'Doctor');

    const result = await MySwal.fire({
      title: approve
        ? t('doctorReview.approve_title', 'Verify this doctor?')
        : t('doctorReview.reject_title', 'Reject this listing?'),
      html: approve
        ? t('doctorReview.approve_text', 'Approving gives them the Verified badge patients see on their card and profile. They are told by e-mail and in the app.')
        : t('doctorReview.reject_text', 'A rejected doctor is hidden from every patient in the platform-wide directory. Their account keeps working; they see your reason and can send corrected details.'),
      icon: approve ? 'question' : 'warning',
      input: 'textarea',
      inputLabel: approve
        ? t('doctorReview.note_label_approve', 'Internal note (optional)')
        : t('doctorReview.note_label_reject', 'Reason for rejection (required)'),
      inputPlaceholder: approve
        ? t('doctorReview.note_ph_approve', 'Anything worth recording about this check…')
        : t('doctorReview.note_ph_reject', 'What was wrong with the registration number or ID proof?'),
      inputAttributes: { 'aria-label': t('doctorReview.note_label_reject', 'Reason for rejection (required)') },
      // Required only on rejection: a rejected doctor is owed a reason they can
      // act on, and it is the only thing written back to their record.
      inputValidator: (value) => (
        (!approve && !String(value || '').trim())
          ? t('doctorReview.note_required', 'Give a reason — the doctor is told why their listing was refused.')
          : undefined
      ),
      showCancelButton: true,
      confirmButtonColor: approve ? '#4ABDBD' : '#dc2626',
      cancelButtonColor: '#e5e7eb',
      confirmButtonText: approve
        ? t('doctorReview.approve_confirm', 'Approve & verify')
        : t('doctorReview.reject_confirm', 'Reject'),
      cancelButtonText: t('doctorReview.cancel', 'Cancel'),
      showLoaderOnConfirm: true,
      preConfirm: async (note) => {
        try {
          const { data } = await axiosInstance.post(
            `${baseURL}/doctor-review/${doctor.doctorId}/decide`,
            { approve, reviewNote: String(note || '').trim() || undefined },
          );
          return data;
        } catch (err) {
          const code = err?.response?.data?.code;
          // Someone else decided this row while the dialog was open. Nothing to
          // retry — the honest response is to close and show the new truth.
          if (code === 'NOT_PENDING') return { conflict: true };
          const message = err?.response?.data?.message;
          MySwal.showValidationMessage(
            typeof message === 'string' ? message : t('doctorReview.decide_error', 'Failed to record the decision.'),
          );
          return false;
        }
      },
      allowOutsideClick: () => !MySwal.isLoading(),
    });

    if (!result.isConfirmed) return;

    setDecidingId(doctor.doctorId);
    if (result.value?.conflict) {
      toast.info(t('doctorReview.conflict', 'Someone already decided this one — the list has been refreshed.'));
    } else {
      toast.success(approve
        ? t('doctorReview.approved_toast', '{{name}} is now verified.', { name: fullName })
        : t('doctorReview.rejected_toast', '{{name}} was rejected.', { name: fullName }));
    }
    await fetchQueue(status, { silent: true });
    setDecidingId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchQueue, status, t]);

  return (
    <div className="w-full" data-testid="doctor-review-container">
      <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-md w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] rounded-xl shadow-sm">
              <BadgeCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-800">
                {t('doctorReview.title', 'Doctor Review')}
              </h1>
              <p className="text-xs sm:text-sm text-gray-500">
                {t('doctorReview.subtitle', 'Credential checks for doctors listing themselves on the Mitra One Care open platform.')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => fetchQueue(status)}
            className="self-start sm:self-auto inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            {t('doctorReview.refresh', 'Refresh')}
          </button>
        </div>

        {/* THE STAKES, stated on the screen itself. Someone opening this cold
            must not have to infer that leaving the queue alone keeps doctors
            hidden from patients indefinitely. */}
        <div className="mt-5 rounded-xl border border-[#4ABDBD]/30 bg-[#4ABDBD]/5 p-4">
          <div className="flex items-start gap-3">
            <EyeOff className="w-5 h-5 text-[#2D8A8A] mt-0.5 shrink-0" />
            <div className="text-sm text-gray-700 leading-relaxed">
              <p className="font-semibold text-[#1a365d]">
                {t('doctorReview.stakes_title', 'Patients see who Mitra One Care has checked.')}
              </p>
              <p className="mt-1">
                {t('doctorReview.stakes_body', 'No hospital vouches for a doctor on the open platform — Mitra One Care does, here. An approved doctor carries a Verified badge for patients; a rejected one is hidden from the platform-wide directory; a doctor not decided yet stays listed without the badge. Each decision reaches the doctor by e-mail and in the app.')}
              </p>
            </div>
          </div>
        </div>

        {/* Status tabs */}
        <div className="flex flex-wrap gap-2 mt-5 mb-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setStatus(tab.id)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                status === tab.id
                  ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white shadow-sm'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
              {status === tab.id && !loading && !loadError && (
                <span className="ml-2 text-xs opacity-90">({doctors.length})</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <CommonLoader />
        ) : loadError ? (
          <div className="text-center py-16">
            <p className="text-sm text-gray-500">{loadError}</p>
            <button
              type="button"
              onClick={() => fetchQueue(status)}
              className="mt-4 px-4 py-2 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg"
            >
              {t('doctorReview.retry', 'Try again')}
            </button>
          </div>
        ) : doctors.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            {status === 'pending_review'
              ? t('doctorReview.empty_pending', 'Nothing waiting — every submitted doctor has been decided.')
              : t('doctorReview.empty_other', 'No doctors with this status.', { status: statusLabel(status) })}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-3 font-semibold">{t('doctorReview.col_doctor', 'Doctor')}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t('doctorReview.col_specialty', 'Specialty')}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t('doctorReview.col_council', 'Council reg. no.')}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t('doctorReview.col_id_proof', 'ID proof')}</th>
                  <th className="text-left px-4 py-3 font-semibold">{t('doctorReview.col_status', 'Status')}</th>
                  {status === 'pending_review' && (
                    <th className="text-right px-4 py-3 font-semibold">{t('doctorReview.col_decision', 'Decision')}</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {doctors.map((d) => (
                  <tr key={d.doctorId} className="hover:bg-[#4ABDBD]/5 transition-colors align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{`${d.name || ''} ${d.surname || ''}`.trim() || t('common.doctor_label', 'Doctor')}</div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                        {d.email || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="inline-flex items-center gap-1.5">
                        <Stethoscope className="w-3.5 h-3.5 text-gray-400" />
                        {specialtyLabel(d.specialty) || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <span className="inline-flex items-center gap-1.5">
                        <IdCard className="w-3.5 h-3.5 text-gray-400" />
                        {d.councilRegistrationNumber || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <IdProofCell doctor={d} onOpen={(e) => openIdProof(d, e)} t={t} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={d.reviewStatus} label={statusLabel(d.reviewStatus)} />
                      <DecisionRow doctor={d} />
                    </td>
                    {status === 'pending_review' && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            disabled={decidingId === d.doctorId}
                            onClick={() => decide(d, true)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                          >
                            <ShieldCheck className="w-3.5 h-3.5" />
                            {t('doctorReview.approve', 'Approve')}
                          </button>
                          <button
                            type="button"
                            disabled={decidingId === d.doctorId}
                            onClick={() => decide(d, false)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 disabled:opacity-50"
                          >
                            <ShieldX className="w-3.5 h-3.5" />
                            {t('doctorReview.reject', 'Reject')}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
